import type { ChatEntry } from "@ark-bot/protocol";

/**
 * A message another one answers, as the bubble above the answer shows it:
 * who said it and the start of what they said, the way WhatsApp quotes a
 * message rather than naming its id. `media` stands in for the words of a
 * message that was only a picture or a file.
 */
export interface Quote {
  readonly id: string;
  /** The user's own message, or an agent's; `author` names a group member. */
  readonly from: "user" | "agent";
  readonly author?: string;
  readonly text: string;
  readonly media: "picture" | "file" | null;
  readonly fileName?: string;
}

/** How much of the quoted message shows; the rest is cut at a word with an ellipsis. */
export const QUOTE_CHARS = 120;

/** Text on one line, cut short at a word boundary where one is near. */
export function clip(text: string, max: number = QUOTE_CHARS): string {
  const line = text.replace(/\s+/g, " ").trim();
  if (line.length <= max) return line;
  const cut = line.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/** The quote for one entry; null for what is not a message (a card, a notice). */
export function quoteOf(entry: ChatEntry | undefined): Quote | null {
  if (entry == null) return null;
  if (entry.kind === "user") {
    return { id: entry.id, from: "user", text: clip(entry.text), media: entry.text.trim().length === 0 && entry.attachments.length > 0 ? "picture" : null };
  }
  if (entry.kind === "agent") {
    const text = clip(entry.text ?? "");
    if (text.length > 0) return { id: entry.id, from: "agent", text, media: null };
    if (entry.images != null && entry.images.length > 0) return { id: entry.id, from: "agent", text, media: "picture" };
    if (entry.file != null) return entry.file.mimeType.startsWith("image/") ? { id: entry.id, from: "agent", text, media: "picture" } : { id: entry.id, from: "agent", text, media: "file", fileName: entry.file.name };
    return { id: entry.id, from: "agent", text, media: null };
  }
  if (entry.kind === "room") return { id: entry.id, from: "agent", author: entry.author.name, text: clip(entry.text), media: null };
  return null;
}

/** The quote for the entry with this id among those loaded; null when it is further back than the page reaches. */
export function findQuote(messages: readonly ChatEntry[], id: string): Quote | null {
  return quoteOf(messages.find((entry) => entry.id === id));
}

/** What "Copy" puts on the clipboard for a message: its words as written, markdown and all. */
export function copyTextOf(entry: ChatEntry): string | null {
  if (entry.kind === "user" || entry.kind === "room") return entry.text.length > 0 ? entry.text : null;
  if (entry.kind === "agent") return entry.text != null && entry.text.length > 0 ? entry.text : null;
  return null;
}

/** How far a message is pulled right before letting go answers it, as WhatsApp's swipe to reply. */
export const SWIPE_REPLY_PX = 56;
/** The furthest a message follows the finger; past the reply point it drags, then stops. */
const SWIPE_MAX_PX = 84;

/** Where the message sits for a finger this far right of where it started: with the finger up to the reply point, then heavier. */
export function swipePull(dx: number): number {
  if (dx <= 0) return 0;
  if (dx <= SWIPE_REPLY_PX) return dx;
  return Math.min(SWIPE_MAX_PX, SWIPE_REPLY_PX + (dx - SWIPE_REPLY_PX) * 0.35);
}

/** Whether a finger's first move sideways is a swipe to reply rather than a scroll: rightwards, and more across than down. */
export function swipeStarts(dx: number, dy: number): boolean {
  return dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.5;
}
